/* * One row of an error's `details`. */
export type ErrDetail = {
  field?: string;
  message?: string;
  reason?: string;
  detail?: string;
  /* * Named ids or row numbers on a target-results refusal. */
  ids?: string[];
  rows?: Array<string | number>;
  count?: number;
};

export class ApiError extends Error {
  code: string;
  /** HTTP status when the envelope came from a non-2xx response. */
  status?: number;
  /*
   * The envelope's `details` carries two shapes: ERR_VALIDATION's `{field, message}`
   * and ERR_MERGE_REFUSED's `{reason, detail}` rows. Optional keys rather than a
   * union, so a narrower type cannot drop the merge refusals before the UI sees them.
   */
  details?: ErrDetail[];
  /** Envelope `error.section`, when the API sent one (join-code refusals). */
  section?: string;
  constructor(
    code: string,
    message: string,
    details?: ErrDetail[],
    status?: number,
    section?: string,
  ) {
    super(message);
    this.code = code;
    this.details = details;
    this.status = status;
    this.section = section;
  }
}

type WireError = {
  code: string;
  message: string;
  details?: ErrDetail[];
  section?: string;
};

/** The `{data, error, meta}` body. Both the hand client and the generated client use this. */
export type WireEnvelope<T> = {
  data?: T | null;
  error?: unknown;
  meta?: unknown;
};

function asError(error: unknown): WireError | undefined {
  const value = Array.isArray(error) ? error[0] : error;
  if (!value || typeof value !== "object") return undefined;
  const record = value as {
    code?: unknown;
    message?: unknown;
    details?: ErrDetail[];
    section?: unknown;
  };
  if (typeof record.code !== "string" || typeof record.message !== "string")
    return undefined;
  return {
    code: record.code,
    message: record.message,
    details: record.details,
    section: typeof record.section === "string" ? record.section : undefined,
  };
}

export function isWireEnvelope(value: unknown): value is WireEnvelope<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    "meta" in value &&
    ("data" in value || "error" in value)
  );
}

export function unwrap<T>(env: WireEnvelope<T>): T {
  const error = asError(env.error);
  if (error) {
    const status = (env as { httpStatus?: number }).httpStatus;
    throw new ApiError(
      error.code,
      error.message,
      error.details,
      status,
      error.section,
    );
  }
  return env.data as T;
}
